/**
 * PlatformBridge — Silent SSO from the unified PLATFORM (Slice 3 consumer).
 * Receives a one-time opaque bridge code via URL, exchanges it for a Foundations coordinator
 * session through the backend (server-to-server verification with the platform), then routes in.
 *
 * URL: /auth/platform-bridge?code=<opaqueCode>&redirect=<path>
 *
 * Security: the code is opaque (not a JWT), single-use, short-TTL. The auth payload never appears
 * in the URL or browser history. Mirror of PRISM's PlatformBridgeAuth.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { forgetPreviousUser } from '../../services/signInState';
import { FORCED_CHANGE_PATH, recordPasswordChangeRequired } from './account/passwordChangeRequired';

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'http://localhost:5001'
    : window.location.origin);
const API = `${API_BASE}/api`;

export default function PlatformBridge() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const redirect = searchParams.get('redirect') || '/coordinator-dashboard';

    if (!code) {
      setError('No bridge code provided');
      return;
    }

    async function authenticate() {
      try {
        const resp = await fetch(`${API}/auth/platform-bridge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        const data = await resp.json();
        if (!resp.ok || !data.success) {
          setError(data.error || 'Platform sign-in failed');
          return;
        }

        const { token, user } = data.data;
        // Mirror the coordinator-login storage so the app recognizes the session.
        forgetPreviousUser();
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userRole', user.role || 'coordinator');
        localStorage.setItem('authToken', token);
        localStorage.setItem('token', token);
        localStorage.setItem('foundations-token', token);
        if (user.id != null) localStorage.setItem('userId', String(user.id));
        if (user.email) localStorage.setItem('userEmail', user.email);
        if (user.company_name) localStorage.setItem('companyName', user.company_name);
        if (user.company_id != null) localStorage.setItem('companyId', String(user.company_id));
        if (user.is_superadmin) localStorage.setItem('isSuperadmin', 'true');
        // A superadmin-issued password lands on the forced change screen before the requested page.
        recordPasswordChangeRequired(user.must_change_password === true);
        if (user.must_change_password === true) {
          navigate(FORCED_CHANGE_PATH, { replace: true });
          return;
        }

        navigate(data.data.redirectPath || redirect, { replace: true });
      } catch (err) {
        setError(err.message || 'Failed to connect to Foundations');
      }
    }

    authenticate();
  }, [searchParams, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        background: '#0a192f',
        color: '#e6f1ff',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      {error ? (
        <>
          <div style={{ color: '#ff4444', maxWidth: 440 }} role="alert">
            {error}
          </div>
          <div style={{ color: '#8892b0', fontSize: '0.85rem' }}>
            This link may have expired. Please return to the platform and try again.
          </div>
        </>
      ) : (
        <div style={{ color: '#8892b0' }}>Signing you in…</div>
      )}
    </div>
  );
}
